'use client';

import { useState, useEffect } from 'react';
import ReviewCard from './ReviewCard';
import RatingDistribution from './RatingDistribution';
import SocialFeed from './SocialFeed';
import ReviewForm, { ReviewFormData } from './ReviewForm';
import StatsCard from './StatsCard';
import Icon from '@/components/ui/AppIcon';

interface Review {
  id: number;
  customerName: string;
  customerImage: string;
  customerImageAlt: string;
  rating: number;
  date: string;
  reviewText: string;
  dishOrdered: string;
  images?: {
    url: string;
    alt: string;
  }[];
  verified: boolean;
  helpful: number;
}

interface RatingData {
  stars: number;
  count: number;
  percentage: number;
}

interface SocialPost {
  id: number;
  platform: 'instagram' | 'facebook' | 'twitter';
  username: string;
  userHandle: string;
  userImage: string;
  userImageAlt: string;
  postDate: string;
  content: string;
  image?: {
    url: string;
    alt: string;
  };
  likes: number;
  comments: number;
  shares: number;
}

const mockReviews: Review[] = [
{
  id: 1,
  customerName: "Priya Sharma",
  customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1c48da832-1763299032357.png",
  customerImageAlt: "Young Indian woman with long black hair smiling warmly at camera in casual attire",
  rating: 5,
  date: "2 days ago",
  reviewText: "Absolutely amazing! The broasted chicken was perfectly crispy on the outside and juicy inside. The Arabian spices blend is just perfect. Best chicken I've had in Bangalore!",
  dishOrdered: "Broast Chicken Meal",
  images: [
  {
    url: "https://images.unsplash.com/photo-1653067824409-4924ce8bb1d6",
    alt: "Golden crispy broasted chicken pieces with herbs on white plate with side of fries"
  },
  {
    url: "https://images.unsplash.com/photo-1734949160721-c9bf35d66fd1",
    alt: "Close-up of juicy fried chicken drumstick with crispy coating and garnish"
  }],

  verified: true,
  helpful: 24
},
{
  id: 2,
  customerName: "Rahul Mehta",
  customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6fad4bf-1763295560698.png",
  customerImageAlt: "Young Indian man with short black hair and beard in blue shirt smiling confidently",
  rating: 5,
  date: "5 days ago",
  reviewText: "The wings are incredible! Tried the spicy Arabian wings and they were finger-licking good. Great portion sizes and the service was quick. Will definitely order again!",
  dishOrdered: "Spicy Arabian Wings",
  images: [
  {
    url: "https://images.unsplash.com/photo-1709556722349-4ed11c9f8f28",
    alt: "Plate of golden buffalo wings with celery sticks and ranch dipping sauce"
  }],

  verified: true,
  helpful: 18
},
{
  id: 3,
  customerName: "Anjali Reddy",
  customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_10cba5570-1763296368383.png",
  customerImageAlt: "Professional Indian woman with shoulder-length hair in business casual attire smiling",
  rating: 4,
  date: "1 week ago",
  reviewText: "Really good food! The chicken was fresh and flavorful. Only reason for 4 stars is the delivery took a bit longer than expected, but the quality made up for it.",
  dishOrdered: "Family Combo Pack",
  verified: true,
  helpful: 12
},
{
  id: 4,
  customerName: "Vikram Singh",
  customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_18162979e-1763294376572.png",
  customerImageAlt: "Young Indian man with styled hair in casual t-shirt looking at camera with friendly expression",
  rating: 5,
  date: "1 week ago",
  reviewText: "Best late-night food spot! Open till 1 AM and the quality never disappoints. The garlic mayo dip is addictive. Highly recommend for anyone craving good chicken.",
  dishOrdered: "Midnight Munch Combo",
  images: [
  {
    url: "https://images.unsplash.com/photo-1699565769152-f71fbca27a51",
    alt: "Basket of crispy fried chicken pieces with golden brown coating and herbs"
  }],

  verified: true,
  helpful: 31
},
{
  id: 5,
  customerName: "Sneha Patel",
  customerImage: "https://images.unsplash.com/photo-1652396944757-ad27b62b33f6",
  customerImageAlt: "Young Indian woman with curly hair in yellow top smiling brightly outdoors",
  rating: 5,
  date: "2 weeks ago",
  reviewText: "Ordered for a family gathering and everyone loved it! The variety in the menu is great. The chicken was perfectly cooked and the sides were delicious too.",
  dishOrdered: "Party Pack",
  verified: true,
  helpful: 15
},
{
  id: 6,
  customerName: "Arjun Kumar",
  customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_18e8b8ea8-1763295802233.png",
  customerImageAlt: "Young Indian man with glasses in casual shirt smiling warmly at camera",
  rating: 4,
  date: "2 weeks ago",
  reviewText: "Great taste and good value for money. The chicken is always fresh and the spice level is just right. Would love to see more vegetarian options though!",
  dishOrdered: "Classic Broast Meal",
  verified: true,
  helpful: 9
}];


const mockRatingData: RatingData[] = [
{ stars: 5, count: 342, percentage: 78 },
{ stars: 4, count: 67, percentage: 15 },
{ stars: 3, count: 18, percentage: 4 },
{ stars: 2, count: 9, percentage: 2 },
{ stars: 1, count: 4, percentage: 1 }];


const mockSocialPosts: SocialPost[] = [
{
  id: 1,
  platform: 'instagram',
  username: "Foodie Bangalore",
  userHandle: "@foodiebangalore",
  userImage: "https://images.unsplash.com/photo-1730581104398-0cc2b5ba4499",
  userImageAlt: "Food blogger profile picture showing person holding camera in restaurant setting",
  postDate: "3 hours ago",
  content: "Just discovered this gem! The broasted chicken at @brochickdigital is absolutely divine. That crispy coating with Arabian spices is everything! 🍗✨ #BangaloreFood #ChickenLovers",
  image: {
    url: "https://images.unsplash.com/photo-1586793783658-261cddf883ef",
    alt: "Instagram food photo of golden broasted chicken pieces artfully arranged on white plate"
  },
  likes: 1247,
  comments: 89,
  shares: 34
},
{
  id: 2,
  platform: 'facebook',
  username: "Bangalore Food Reviews",
  userHandle: "BangaloreFoodReviews",
  userImage: "https://img.rocket.new/generatedImages/rocket_gen_img_174caec63-1764671621579.png",
  userImageAlt: "Food review page profile showing multiple food dishes in collage format",
  postDate: "1 day ago",
  content: "Rating: 4.5/5 ⭐\n\nBro Chick Digital delivers on taste and quality! Their Arabian wings are a must-try. Perfect for late-night cravings as they're open till 1 AM!",
  image: {
    url: "https://images.unsplash.com/photo-1625188186646-2cec41dc8760",
    alt: "Facebook post image showing plate of spicy chicken wings with dipping sauces"
  },
  likes: 892,
  comments: 56,
  shares: 23
},
{
  id: 3,
  platform: 'twitter',
  username: "BLR Eats",
  userHandle: "@blreats",
  userImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1d78c5cab-1764671621669.png",
  userImageAlt: "Twitter food account profile picture with fork and knife icon on colorful background",
  postDate: "2 days ago",
  content: "Late night hunger sorted! @BroChickDigital never disappoints. That garlic mayo dip though 😍 #BangaloreNightLife #FoodDelivery",
  likes: 456,
  comments: 28,
  shares: 12
},
{
  id: 4,
  platform: 'instagram',
  username: "Spice Route Stories",
  userHandle: "@spiceroutestories",
  userImage: "https://images.unsplash.com/photo-1651803456072-7c3362e4a8df",
  userImageAlt: "Food influencer profile picture showing person in chef's apron in modern kitchen",
  postDate: "3 days ago",
  content: "The authentic Arabian spice blend at Bro Chick is what sets them apart! You can taste the quality in every bite. Highly recommend their family combo! 🌶️👨‍👩‍👧‍👦",
  image: {
    url: "https://images.unsplash.com/photo-1690965045742-41d0624468a2",
    alt: "Instagram story showing family sharing large platter of fried chicken pieces"
  },
  likes: 2103,
  comments: 145,
  shares: 67
}];


export default function ReviewsInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [reviews, setReviews] = useState<Review[]>(mockReviews);
  const [sortBy, setSortBy] = useState<'recent' | 'helpful' | 'rating'>('recent');
  const [filterRating, setFilterRating] = useState<number | 'all'>('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:236:6" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="236" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:237:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="237" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-8%22%7D" className="container mx-auto px-4 py-8">
          <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:238:10" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="238" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:239:12" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="239" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-12 bg-muted rounded w-1/3"></div>
            <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:240:12" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="240" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) =>
              <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:242:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="242" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-muted%20rounded%22%7D" key={i} className="h-32 bg-muted rounded"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  const handleHelpful = (id: number) => {
    setReviews(reviews.map((review) =>
    review.id === id ? { ...review, helpful: review.helpful + 1 } : review
    ));
  };

  const handleReviewSubmit = (data: ReviewFormData) => {
    const newReview: Review = {
      id: reviews.length + 1,
      customerName: data.name,
      customerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff98651f-1763298723262.png",
      customerImageAlt: "New customer profile picture showing friendly smiling person",
      rating: data.rating,
      date: "Just now",
      reviewText: data.reviewText,
      dishOrdered: data.dishOrdered || "Not specified",
      verified: false,
      helpful: 0
    };
    setReviews([newReview, ...reviews]);
  };

  const sortedReviews = [...reviews].sort((a, b) => {
    if (sortBy === 'helpful') return b.helpful - a.helpful;
    if (sortBy === 'rating') return b.rating - a.rating;
    return 0;
  });

  const filteredReviews = filterRating === 'all' ?
  sortedReviews :
  sortedReviews.filter((review) => review.rating === filterRating);

  return (
    <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:284:4" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="284" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-12%22%7D" className="space-y-12">
      {/* Stats Section */}
      <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:286:6" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="286" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:287:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="287" data-component-file="ReviewsInteractive.tsx" data-component-name="StatsCard" data-component-content="%7B%22elementName%22%3A%22StatsCard%22%2C%22value%22%3A%224.3%22%7D"
          icon="StarIcon"
          label="Average Rating"
          value="4.3"
          trend="+0.2"
          trendUp={true} />

        <StatsCard data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:294:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="294" data-component-file="ReviewsInteractive.tsx" data-component-name="StatsCard" data-component-content="%7B%22elementName%22%3A%22StatsCard%22%2C%22value%22%3A%22440%22%7D"
          icon="ChatBubbleLeftRightIcon"
          label="Total Reviews"
          value="440"
          trend="+12%"
          trendUp={true} />

        <StatsCard data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:301:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="301" data-component-file="ReviewsInteractive.tsx" data-component-name="StatsCard" data-component-content="%7B%22elementName%22%3A%22StatsCard%22%2C%22value%22%3A%2212.5K%22%7D"
          icon="UserGroupIcon"
          label="Social Followers"
          value="12.5K"
          trend="+8%"
          trendUp={true} />

        <StatsCard data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:308:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="308" data-component-file="ReviewsInteractive.tsx" data-component-name="StatsCard" data-component-content="%7B%22elementName%22%3A%22StatsCard%22%2C%22value%22%3A%2298%25%22%7D"
          icon="HeartIcon"
          label="Customer Love"
          value="98%"
          trend="+3%"
          trendUp={true} />

      </div>

      {/* Rating Distribution & Review Form */}
      <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:318:6" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="318" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-8%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <RatingDistribution data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:319:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="319" data-component-file="ReviewsInteractive.tsx" data-component-name="RatingDistribution" data-component-content="%7B%22elementName%22%3A%22RatingDistribution%22%7D"
          ratings={mockRatingData}
          averageRating={4.3}
          totalReviews={440} />

        <ReviewForm data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:324:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="324" data-component-file="ReviewsInteractive.tsx" data-component-name="ReviewForm" data-component-content="%7B%22elementName%22%3A%22ReviewForm%22%7D" onSubmit={handleReviewSubmit} />
      </div>

      {/* Reviews Section */}
      <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:328:6" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="328" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
        <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:329:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="329" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20md%3Aflex-row%20md%3Aitems-center%20md%3Ajustify-between%20mb-6%20space-y-4%20md%3Aspace-y-0%22%7D" className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
          <h2 data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:330:10" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="330" data-component-file="ReviewsInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20font-headline%20font-headline-bold%20text-text-primary%22%2C%22textContent%22%3A%22Customer%20Reviews%22%7D" className="text-3xl font-headline font-headline-bold text-text-primary">
            Customer Reviews
          </h2>

          <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:334:10" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="334" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20items-center%20gap-3%22%7D" className="flex flex-wrap items-center gap-3">
            {/* Filter by Rating */}
            <select data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:336:12" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="336" data-component-file="ReviewsInteractive.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22value%22%3A%22%5Bvar%3AfilterRating%5D%22%2C%22className%22%3A%22px-4%20py-2%20bg-input%20border%20border-border%20rounded-md%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%20font-body%22%7D"
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="px-4 py-2 bg-input border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-body">

              <option data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:341:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="341" data-component-file="ReviewsInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22all%22%2C%22textContent%22%3A%22All%20Ratings%22%7D" value="all">All Ratings</option>
              <option data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:342:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="342" data-component-file="ReviewsInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22textContent%22%3A%225%20Stars%22%7D" value={5}>5 Stars</option>
              <option data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:343:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="343" data-component-file="ReviewsInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22textContent%22%3A%224%20Stars%22%7D" value={4}>4 Stars</option>
              <option data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:344:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="344" data-component-file="ReviewsInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22textContent%22%3A%223%20Stars%22%7D" value={3}>3 Stars</option>
              <option data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:345:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="345" data-component-file="ReviewsInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22textContent%22%3A%222%20Stars%22%7D" value={2}>2 Stars</option>
              <option data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:346:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="346" data-component-file="ReviewsInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22textContent%22%3A%221%20Star%22%7D" value={1}>1 Star</option>
            </select>

            {/* Sort By */}
            <select data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:350:12" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="350" data-component-file="ReviewsInteractive.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22value%22%3A%22%5Bvar%3AsortBy%5D%22%2C%22className%22%3A%22px-4%20py-2%20bg-input%20border%20border-border%20rounded-md%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%20font-body%22%7D"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'recent' | 'helpful' | 'rating')}
              className="px-4 py-2 bg-input border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-body">

              <option data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:355:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="355" data-component-file="ReviewsInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22recent%22%2C%22textContent%22%3A%22Most%20Recent%22%7D" value="recent">Most Recent</option>
              <option data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:356:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="356" data-component-file="ReviewsInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22helpful%22%2C%22textContent%22%3A%22Most%20Helpful%22%7D" value="helpful">Most Helpful</option>
              <option data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:357:14" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="357" data-component-file="ReviewsInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22rating%22%2C%22textContent%22%3A%22Highest%20Rating%22%7D" value="rating">Highest Rating</option>
            </select>
          </div>
        </div>

        <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:362:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="362" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredReviews.map((review) =>
          <ReviewCard data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:364:10" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="364" data-component-file="ReviewsInteractive.tsx" data-component-name="ReviewCard" data-component-content="%7B%22elementName%22%3A%22ReviewCard%22%7D" key={review.id} review={review} onHelpful={handleHelpful} />
          )}
        </div>
      </div>

      {/* Social Media Feed */}
      <SocialFeed data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:370:6" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="370" data-component-file="ReviewsInteractive.tsx" data-component-name="SocialFeed" data-component-content="%7B%22elementName%22%3A%22SocialFeed%22%7D" posts={mockSocialPosts} />

      {/* CTA Section */}
      <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:373:6" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="373" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-gradient-to-r%20from-primary%20to-primary%2F80%20rounded-lg%20p-8%20md%3Ap-12%20text-center%22%7D" className="bg-gradient-to-r from-primary to-primary/80 rounded-lg p-8 md:p-12 text-center">
        <h2 data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:374:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="374" data-component-file="ReviewsInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-headline%20font-headline-bold%20text-primary-foreground%20mb-4%22%2C%22textContent%22%3A%22Join%20Our%20Food%20Community%22%7D" className="text-3xl md:text-4xl font-headline font-headline-bold text-primary-foreground mb-4">
          Join Our Food Community
        </h2>
        <p data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:377:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="377" data-component-file="ReviewsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-primary-foreground%2F90%20font-body%20mb-6%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Share%20your%20Bro%20Chick%20experience%20on%20social%20media%20and%20tag%20us%20to%20be%20featured!%22%7D" className="text-lg text-primary-foreground/90 font-body mb-6 max-w-2xl mx-auto">
          Share your Bro Chick experience on social media and tag us to be featured!
        </p>
        <div data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:380:8" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="380" data-component-file="ReviewsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20items-center%20justify-center%20gap-4%22%7D" className="flex flex-wrap items-center justify-center gap-4">
          <a data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:381:10" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="381" data-component-file="ReviewsInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22https%3A%2F%2Finstagram.com%2Fbrochickdigital%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-6%20py-3%20bg-white%20text-primary%20rounded-md%20hover%3Abg-white%2F90%20transition-smooth%20shadow-brand%20font-cta%22%7D"
            href="https://instagram.com/brochickdigital"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-6 py-3 bg-white text-primary rounded-md hover:bg-white/90 transition-smooth shadow-brand font-cta">

            <Icon data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:387:12" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="387" data-component-file="ReviewsInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CameraIcon%22%7D" name="CameraIcon" size={20} />
            <span data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:388:12" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="388" data-component-file="ReviewsInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Follow%20on%20Instagram%22%7D">Follow on Instagram</span>
          </a>
          <a data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:390:10" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="390" data-component-file="ReviewsInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22https%3A%2F%2Ffacebook.com%2Fbrochickdigital%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-6%20py-3%20bg-white%20text-primary%20rounded-md%20hover%3Abg-white%2F90%20transition-smooth%20shadow-brand%20font-cta%22%7D"
            href="https://facebook.com/brochickdigital"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-6 py-3 bg-white text-primary rounded-md hover:bg-white/90 transition-smooth shadow-brand font-cta">

            <Icon data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:396:12" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="396" data-component-file="ReviewsInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22UserGroupIcon%22%7D" name="UserGroupIcon" size={20} />
            <span data-component-id="src/app/reviews-community/components/ReviewsInteractive.tsx:397:12" data-component-path="src/app/reviews-community/components/ReviewsInteractive.tsx" data-component-line="397" data-component-file="ReviewsInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Like%20on%20Facebook%22%7D">Like on Facebook</span>
          </a>
        </div>
      </div>
    </div>);

}